import React, { useEffect, useState } from 'react';
import { Box, Grid, TextField, Typography, Button } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
	LocalizationProvider,
	MobileDateTimePicker,
	TimePicker,
} from '@mui/x-date-pickers';
import styles from './IncidentTab.module.css';
import { format } from 'date-fns';
import { useScheduleContext } from 'containers/pages/CANguard/context/ScheduleProvider';
import { useGuardRoleContext } from 'containers/pages/CANguard/context/GuardRoleProvider';

interface StartTimeSelectionProps {
	startTime: Date | null;
	handleStartTimeChange: (newTime: Date | null) => void;
	startTimeInputError: string | undefined;
}
interface StartTimeConstraints {
	disableTime: boolean;
	minTime: Date | null;
	message: string;
}
const StartTimeSelection: React.FC<StartTimeSelectionProps> = (props) => {
	const { startTime, handleStartTimeChange, startTimeInputError } = props;
	const { isAdmin, guardSelectorOptions } = useGuardRoleContext();
	const { schedule } = useScheduleContext();
	const [startTimeConstraints, setStartTimeConstraints] =
		useState<StartTimeConstraints>({
			disableTime: true,
			minTime: new Date(), // set to yesterday
			message: '',
		});

	useEffect(() => {
		if (schedule) {
			let filteredPatrols = schedule.filter(
				(patrol) => patrol.type !== 'Incident',
			);
			if (filteredPatrols.length > 0) {
				configureStartTimeConstraints(
					new Date(filteredPatrols[0].start_time),
				);
			} else {
				configureStartTimeConstraints(null);
			}
		}
	}, [schedule]);

	const configureStartTimeConstraints = (earliestStartTime: Date | null) => {
		const now = new Date();

		let constraints: StartTimeConstraints;

		if (!earliestStartTime) {
			constraints = {
				disableTime: true,
				minTime: null,
				message: 'You cannot set time. There are no patrols scheduled.',
			};
		} else if (earliestStartTime < now) {
			constraints = {
				disableTime: false,
				minTime: earliestStartTime,
				message: `Please select a time between ${format(
					earliestStartTime,
					'MMM dd yyyy hh:mm a',
				)} and the current time.`,
			};
		} else {
			constraints = {
				disableTime: true,
				minTime: earliestStartTime,
				message: `Can only set time after first patrol. Try again after ${format(
					earliestStartTime,
					'MMM dd yyyy hh:mm a',
				)}`,
			};
		}

		if (isAdmin) {
			constraints = {
				disableTime: false,
				minTime: null,
				message: '',
			};
		}

		setStartTimeConstraints(constraints);
	};

	const handleAdminTimeChange = (newTime: Date | null) => {
		// Get the selected date from guardSelectorOptions.selectedDate
		let date = null;
		if (guardSelectorOptions.selectedDate) {
			date = new Date(guardSelectorOptions.selectedDate);
		}
		if (newTime && date) {
			// Extract hours and minutes from newTime
			const hours = newTime.getHours();
			const minutes = newTime.getMinutes();
			date.setHours(hours, minutes);
			handleStartTimeChange(date);
		}
	};

	return (
		<Grid item className={styles.sectionContainer}>
			<Typography component="div">
				Enter Start Time:{' '}
				<span className={styles.timeInfo}>
					{isAdmin &&
						`(Date:
						${format(
							guardSelectorOptions?.selectedDate || new Date(),
							'EEE, d MMM yyyy',
						)})`}
				</span>
			</Typography>
			<LocalizationProvider dateAdapter={AdapterDateFns}>
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						gap: '10px',
					}}
				>
					{' '}
					{isAdmin ? (
						<TimePicker
							value={startTime || null}
							onChange={(newTime) =>
								handleAdminTimeChange(newTime)
							}
							label="Start Time"
							inputFormat="h:mm a"
							disableMaskedInput // Disable masked input
							renderInput={(params) => <TextField {...params} />}
						/>
					) : (
						<MobileDateTimePicker
							value={startTime || null}
							onChange={(newTime) =>
								handleStartTimeChange(newTime as Date | null)
							}
							label="Start Time"
							inputFormat="h:mm a"
							mask="__:__ _M"
							minDateTime={startTimeConstraints.minTime}
							maxDateTime={new Date()}
							renderInput={(params) => (
								<TextField
									{...params}
									error={!!startTimeInputError}
									helperText={startTimeInputError}
								/>
							)}
							disabled={startTimeConstraints.disableTime}
						/>
					)}
					<Box>
						<Button
							variant="contained"
							color="primary"
							onClick={() =>
								isAdmin
									? handleAdminTimeChange(new Date())
									: handleStartTimeChange(new Date())
							}
							disabled={startTimeConstraints.disableTime}
						>
							Set to Now
						</Button>
					</Box>
				</div>

				<Typography
					component="div"
					color={
						startTimeConstraints.disableTime ? 'error' : 'initial'
					}
				>
					{startTimeConstraints.message}
				</Typography>
			</LocalizationProvider>
		</Grid>
	);
};

export default StartTimeSelection;
